import { Link } from "@tanstack/react-router"
import { ArrowUpRight, NotebookTabs } from "lucide-react"
import { useMemo } from "react"
import { Skeleton } from "@/components/ui/skeleton"
import type { Scope } from "@/lib/connection"
import { useFormatNumber, useT } from "@/lib/i18n"
import { shortHash } from "@/lib/utils"
import type { Ontology } from "@/workspace/ontology"
import { RefusalNote } from "@/workspace/values"
import { TYPES, WORKBOOK_ID, toDecision, toErpRow, toShopRow, useAllObjects, useCustomerNames } from "./data"
import { decisionKey, match, needsReview, summarize } from "./match"
import { PAYMENTS_WORKBOOK_ID } from "./payments/data"
import { ORDER_SYNC } from "./promote"

/**
 * Sources › Sync health: each promoted sync with its generated mapping, the checker's verdict and
 * whether it is installed, beside the live state of the match it came from.
 */
export function SyncHealth({ scope, ontology, installedMappings }: { scope: Scope; ontology: Ontology; installedMappings: ReadonlySet<string> }) {
  const t = useT()
  const formatNumber = useFormatNumber()
  const enabled = ontology.types.has(TYPES.sync)
  const syncs = useAllObjects(scope, TYPES.sync, enabled)
  const live = useOrderMatchCounts(scope, ontology)
  if (!enabled) return null
  return (
    <section className="space-y-3">
      <h2 className="text-[15px] font-semibold tracking-tight">{t("wb.health.title")}</h2>
      {syncs.isError ? (
        <RefusalNote error={syncs.error} title={t("wb.readRefused")} />
      ) : syncs.isPending ? (
        <Skeleton className="h-16 w-full rounded-2xl" />
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border text-sm">
          {syncs.data.items.map((o) => {
            const v = o.values as Record<string, unknown>
            const mapping = `${String(v.mapping_id)}@${String(v.mapping_version)}`
            const isLive = installedMappings.has(mapping)
            // The order match's live counts belong to its own sync; a payments sync opens its own workbook.
            const orders = String(v.sync_id) === ORDER_SYNC.syncId
            const workbookId = orders ? WORKBOOK_ID : PAYMENTS_WORKBOOK_ID
            return (
              <li key={o.key} className="grid gap-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                <div className="min-w-0 space-y-0.5">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{String(v.title)}</span>
                    <span className={`inline-flex h-5 items-center rounded-full border px-2 text-2xs ${isLive ? "border-success/40 text-success" : "border-border text-muted-foreground"}`}>
                      {isLive ? t("wb.health.live") : t("wb.health.proposed")}
                    </span>
                    {v.admitted === true ? <span className="text-2xs text-success">{t("wb.health.admitted")}</span> : <span className="text-2xs text-destructive">{t("wb.health.notAdmitted")}</span>}
                  </p>
                  <p dir="ltr" className="font-mono text-2xs text-muted-foreground">
                    {mapping} · sha256 {shortHash(String(v.mapping_sha256))} · {String(v.source_system)} → {String(v.target_system)}
                  </p>
                  <p className="text-xs text-muted-foreground">{String(v.rules)}</p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  {live && orders ? (
                    <span className="text-muted-foreground tabular-nums">
                      {t("wb.health.now", { matched: formatNumber(live.matched), review: formatNumber(live.review), unmatched: formatNumber(live.unmatched) })}
                    </span>
                  ) : null}
                  <Link to="/$orgId/$environmentId/data/workbooks/$workbookId" params={{ ...scope, workbookId }} search={{}} className="inline-flex items-center gap-1 hover:underline">
                    <NotebookTabs className="size-3.5" aria-hidden /> {t("wb.health.open")} <ArrowUpRight className="size-3 rtl:-scale-x-100" aria-hidden />
                  </Link>
                </div>
              </li>
            )
          })}
          {syncs.data.items.length === 0 ? (
            <li className="px-4 py-3 text-sm text-muted-foreground">
              {t("wb.health.none")}{" "}
              <Link to="/$orgId/$environmentId/data/workbooks/$workbookId" params={{ ...scope, workbookId: WORKBOOK_ID }} search={{}} className="underline underline-offset-4">
                {t("wb.health.openWorkbook")}
              </Link>
            </li>
          ) : null}
        </ul>
      )}
    </section>
  )
}

/** The order match's current counts, from the same cached reads the workbook uses. */
export function useOrderMatchCounts(scope: Scope, ontology: Ontology | undefined) {
  const enabled = Boolean(ontology?.types.has(TYPES.shop) && ontology.types.has(TYPES.erp))
  const shop = useAllObjects(scope, TYPES.shop, enabled)
  const erp = useAllObjects(scope, TYPES.erp, enabled)
  const hasDecisions = Boolean(ontology?.types.has(TYPES.decision))
  const decisions = useAllObjects(scope, TYPES.decision, enabled && hasDecisions)
  const customers = useCustomerNames(scope, enabled && Boolean(ontology?.types.has(TYPES.customer)))
  return useMemo(() => {
    // Counts only from complete reads, so a refused or capped sheet never reports false work.
    // A failed refresh keeps its old data in the cache: errors gate as well as missing or capped reads.
    if (shop.isError || erp.isError || !shop.data || !erp.data || shop.data.truncated || erp.data.truncated) return null
    if (hasDecisions && (decisions.isError || !decisions.data || decisions.data.truncated)) return null
    if (!customers.settled) return null
    const decided = new Set((decisions.data?.items ?? []).flatMap((o) => (toDecision(o) ? [o.key] : [])))
    const erpRows = erp.data.items.map((o) => toErpRow(o, customers.names))
    const pairs = match(shop.data.items.map(toShopRow), erpRows)
    const s = summarize(pairs, erpRows)
    const unmatched = s.byReason.amount_mismatch + s.byReason.duplicate + s.byReason.missing_in_erp + s.byReason.missing_in_shopify
    return { matched: s.clean, review: pairs.filter((p) => needsReview(p) && !decided.has(decisionKey(p))).length, unmatched, missingInErp: s.byReason.missing_in_erp }
  }, [shop.data, erp.data, decisions.data, hasDecisions, shop.isError, erp.isError, decisions.isError, customers.settled, customers.names])
}
